import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import Preloader from '../components/Preloader';
import Footer from '../components/Footer';
import { FaSearch } from 'react-icons/fa';
import ScrollReveal from '../components/ScrollReveal';

const festivals = [
    {
        id: 1,
        title: 'Homowo Festival',
        region: 'Greater Accra',
        description: 'A harvest festival celebrated by the Ga people with parades and street celebrations.',
        image: '/assets/homowo.jpg',
        fullContent: 'Homowo means "to hoot at hunger" in the Ga language. This vibrant festival celebrates the successful farming season with music, dance, and traditional ceremonies.',
        month: 'August-September',
        images: ['/assets/homowo.jpg', '/assets/homowo.jpg', '/assets/homowo.jpg'],
    },
    {
        id: 2,
        title: 'Cape Town Minstrel Carnival',
        region: 'Western Cape',
        description: 'A vibrant and colourful parade celebrating the "Second New Year" with music, dance, and extravagant costumes.',
        image: '/assets/westerncapefestival.jpg',
        fullContent: 'The Cape Town Minstrel Carnival, also known as Kaapse Klopse, is a historic celebration held on the 2nd of January (Tweede Nuwe Jaar). Thousands of minstrels in brightly coloured costumes parade through the city, playing music and dancing, a tradition rooted in the history of slaves in the Cape being given a day off to celebrate.',
        month: 'January',
        images: ['/assets/westerncapefestival.jpg', '/assets/westerncapefestival.jpg', '/assets/westerncapefestival.jpg'],
    },
    {
        id: 3,
        title: 'Timkat Festival',
        region: 'Addis Ababa',
        description: 'An exodus festival celebrated with colorful processions and water blessing ceremonies.',
        image: '/assets/timakat.jpg',
        fullContent: 'Timkat is one of Ethiopia\'s most important festivals, celebrating Epiphany with colorful processions, traditional music, and water blessing ceremonies.',
        month: 'January',
        images: ['/assets/timakat.jpg', '/assets/timakat.jpg', '/assets/timakat.jpg'],
    },
];

const CulturalFestivals = () => {
    const [filter, setFilter] = useState('All');
    const [searchTerm, setSearchTerm] = useState('');
    const navigate = useNavigate();

    const filteredFestivals = festivals.filter(festival => {
        const matchesRegion = filter === 'All' || festival.region === filter;
        const matchesSearch = festival.title.toLowerCase().includes(searchTerm.toLowerCase()) || festival.description.toLowerCase().includes(searchTerm.toLowerCase());
        return matchesRegion && matchesSearch;
    });

    const regions = ['All', ...new Set(festivals.map(f => f.region))];

    const handleExplore = (festivalId) => {
        navigate(`/festival-detail/${festivalId}`);
    };

    return (
        <div className='font-nixie-one bg-gray-50 min-h-screen text-gray-800'>
            <PageHeader />
            <Preloader />
            <main>
                {/* Hero Section */}
                <section className="relative h-[50vh] bg-cover bg-center" style={{ backgroundImage: "url('/assets/cultural1.webp')" }}>
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                        <div className="text-center text-white px-4">
                            <h1 className="text-5xl md:text-6xl font-cormorant-upright font-bold">Cultural Festivals</h1>
                            <p className="mt-4 text-sm md:text-sm max-w-3xl mx-auto">Experience the vibrant traditions and celebrations that define Ghana's rich cultural heritage.</p>
                        </div>
                    </div>
                </section>

                {/* Festivals Section */}
                <section className="py-20 px-4">
                    <div className="container mx-auto">
                        <div className="flex flex-col md:flex-row justify-between items-center mb-12">
                            <div className="flex space-x-2 md:space-x-4 mb-6 md:mb-0 flex-wrap">
                                {regions.map(region => (
                                    <button key={region} onClick={() => setFilter(region)} className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors duration-300 ${filter === region ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}>
                                        {region}
                                    </button>
                                ))}
                            </div>
                            <div className="relative w-96 md:w-96">
                                <input type="text" placeholder="Search festivals..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-96 pl-10 pr-4 py-1 text-sm border rounded-full focus:outline-none focus:ring-2 focus:ring-green-500" />
                                <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                            </div>
                        </div>

                        <ScrollReveal animationClass="animate-slide-left" threshold={0.1} delay={200}>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                                {filteredFestivals.map((festival) => (
                                    <div key={festival.id} className="bg-white rounded-lg shadow-lg overflow-hidden flex flex-col group">
                                        <div className="relative h-40">
                                            <img src={festival.image} alt={festival.title} className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500" />
                                            <span className="absolute top-2 right-2 bg-green-600 text-white text-xs font-bold px-2 py-1 rounded">{festival.month}</span>
                                        </div>
                                        <div className="p-6 flex flex-col flex-grow">
                                            <h3 className="text-xl font-bold font-cormorant-upright mb-2">{festival.title}</h3>
                                            <p className="text-gray-500 text-sm mb-2">{festival.region}</p>
                                            <p className="text-gray-600 text-sm flex-grow">{festival.description}</p>
                                            <button onClick={() => handleExplore(festival.id)} className="inline-block mt-4 self-start px-5 py-1 text-sm bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors duration-300">
                                                Explore
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </ScrollReveal>
                        {filteredFestivals.length === 0 && (
                            <p className="text-center text-gray-500 mt-8">No festivals found matching your criteria.</p>
                        )}
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
};

export default CulturalFestivals;
